import { Category } from '@@types'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton'
import { useMediaQuery } from 'usehooks-ts'

import { useStrippedPathname } from '@shared/lib/locale'

import stylesLayout from '@pages/catalog/ui/styles.module.scss'

import { CarouselCategoriesPreview, useCategoriesBranch, useCategoriesPreviewProducts, useFetchSingleCategory } from '@entities/Categories'
import {
  ButtonProductFilters,
  ProductFilters,
  Products,
  useProductSort,
} from '@entities/Product'
import { SORT_PRODUCTS } from '@entities/Product/sort'

import { CartPreview } from '@features/cart'
import { useAddressStore } from '@features/shipping-methods'

import { CategoriesSidebar } from '@widgets/categories-branch'
import { ProductCarousel } from '@widgets/product-carousel'

import SEO from '@shared/components/SEO'
import { URL_PAGE_CATALOG } from '@shared/contants'
import { BRAND_NAME } from '@shared/contants/app'
import { cn } from '@shared/lib/cn'
import Breadcrumbs from '@shared/ui/breadcrumbs'
import Container from '@shared/ui/container'
import PageGrid from '@shared/ui/page-grid'
import SelectSort from '@shared/ui/select-sort'
import TitleWithCount from '@shared/ui/title-with-count'
import Wrapper from '@shared/ui/wrapper'
import getNoun from '@shared/uttils/getNoun'

import styles from './children/styles.module.scss'

function ProductCategoryCarouselLoading () {
  return (
    <SkeletonTheme>
      <Skeleton
        count={6}
        width={'100%'}
        style={{
          borderRadius: 12,
          aspectRatio: '4/1',
          marginBottom: 56,
        }}
      />
    </SkeletonTheme>
  )
}

/**
 * Универсальная страница каталога с бесконечной вложенностью категорий
 * Поддерживает любой уровень вложенности: /catalog/cat1/cat2/cat3/cat4/...
 */
export default function DynamicCatalogPage () {
  const { t: tn } = useTranslation('navigation')
  const { t: tp } = useTranslation('product')
  const { t: ts } = useTranslation('seo')

  const isTablet = useMediaQuery('(max-width: 991px)')
  const pathname = useStrippedPathname() // Locale-stripped pathname
  const { shopId } = useAddressStore()
  const { sortKey, setSortKey } = useProductSort()
  const [ totalProducts, setTotalProducts ] = useState<number | null>(null)

  // Извлекаем slug текущей категории из URL (без локали)
  // Например: /catalog/cat1/cat2/cat3 → cat3
  const categorySlug = useMemo(() => {
    const segments = pathname.split('/').filter(Boolean)
    // Убираем 'catalog' и берем последний сегмент
    const catalogIndex = segments.indexOf('catalog')
    if (catalogIndex === -1) return ''
    return segments[segments.length - 1] || ''
  }, [ pathname ])

  // Определяем уровень вложенности (1, 2, 3, ...)
  const categoryLevel = useMemo(() => {
    const segments = pathname.split('/').filter(Boolean)
    const catalogIndex = segments.indexOf('catalog')
    if (catalogIndex === -1) return 0
    // Уровень = количество сегментов после 'catalog'
    return segments.length - catalogIndex - 1
  }, [ pathname ])

  // Загружаем текущую категорию
  const { category, loading: loadingCategory } = useFetchSingleCategory({
    shop_id: shopId,
    slug: categorySlug,
  })

  // Для первого уровня загружаем подкатегории с товарами (карусели)
  const { categories: childrenCategoriesWithProducts, loading: childrenCategoriesLoading } =
    useCategoriesPreviewProducts(
      {
        shop_id: shopId,
        parentId: category?.id,
      },
      { skip: !category?.id || !shopId || categoryLevel !== 1 },
    )

  // Для второго+ уровня загружаем подкатегории для чипсов
  const { categoriesBranch } = useCategoriesBranch({
    shopId,
    parentId: category?.id,
  })

  // Проверяем есть ли подкатегории
  const hasSubCategories = categoryLevel === 1
    ? !childrenCategoriesLoading && childrenCategoriesWithProducts && childrenCategoriesWithProducts.length > 0
    : categoriesBranch && categoriesBranch.length > 0
  const noSubCategories = !hasSubCategories

  // Рекурсивно строим цепочку родителей для breadcrumbs
  const breadcrumbChain = useMemo(() => {
    if (!category) return []

    const chain: Category[] = []
    let current: Category | null | undefined = category

    // Идем от текущей категории вверх по родителям
    while (current) {
      chain.unshift(current)
      current = current.parent
    }

    return chain
  }, [ category ])

  // Строим breadcrumbs из цепочки
  const breadcrumbs = useMemo(() => {
    const links: Array<{ title: string; to?: string }> = [
      {
        title: tn('catalog'),
        to: URL_PAGE_CATALOG,
      },
    ]

    // Добавляем все промежуточные категории
    breadcrumbChain.forEach((cat, index) => {
      // Строим путь для каждого уровня
      const pathSegments = breadcrumbChain.slice(0, index + 1).map(c => c.slug)
      const isLast = index === breadcrumbChain.length - 1

      if (isLast) {
        links.push({
          title: cat.name ?? '',
        })
      }
      else {
        links.push({
          title: cat.name ?? '',
          to: `${URL_PAGE_CATALOG}/${pathSegments.join('/')}`,
        })
      }
    })

    return links
  }, [ breadcrumbChain, tn ])

  // Сбрасываем totalProducts при смене категории
  useEffect(() => {
    setTotalProducts(null)
  }, [ category?.id ])

  return (
    <Container>
      <SEO
        title={category?.name ? `${category.name} | ${BRAND_NAME}` : ts('catalog.title', { brand: BRAND_NAME })}
        description={
          category?.name
            ? ts('catalog.categoryDescription', { category: category.name, brand: BRAND_NAME })
            : ts('catalog.description', { brand: BRAND_NAME })
        }
      />
      <PageGrid aside={<CartPreview />}>
        <Wrapper className={styles.wrapper}>
          <div className={stylesLayout.grid}>
            <Breadcrumbs
              className={stylesLayout.breadcrumbs}
              links={breadcrumbs}
              style={{
                marginBottom: '24px',
              }}
            />

            {!isTablet && (
              <div className={cn(stylesLayout.aside, styles.aside)}>
                <div className={styles.aside}>
                  <CategoriesSidebar className={styles.aside__content} />
                </div>

                {/* Фильтры показываем только если нет подкатегорий или уровень > 1 */}
                {(noSubCategories || categoryLevel > 1) && category?.id && (
                  <ProductFilters categoryId={category?.id} />
                )}
              </div>
            )}

            <div className={cn(stylesLayout.content)}>
              {/* Первый уровень: Заголовок без счетчика */}
              {categoryLevel === 1 ? (
                <h1 className={styles.root__title}>{category?.name}</h1>
              ) : (
                /* Второй+ уровень: Заголовок с счетчиком и сортировкой */
                <div className={styles.root__top}>
                  {category?.name && (
                    <TitleWithCount className={styles.root__title} title={category?.name}>
                      {totalProducts !== null && (
                        <>
                          {totalProducts}{' '}
                          {getNoun(
                            totalProducts as number,
                            tp('product.1'),
                            tp('product.2'),
                            tp('product.3'),
                          )}
                        </>
                      )}
                    </TitleWithCount>
                  )}

                  {isTablet && (
                    <ButtonProductFilters className={styles['root__filter-button']} />
                  )}

                  {(totalProducts === null || totalProducts > 0) && (
                    <SelectSort
                      className={styles.root__sort}
                      fields={SORT_PRODUCTS}
                      value={sortKey}
                      onChange={(key) => {
                        setSortKey(key)
                      }}
                    />
                  )}
                </div>
              )}

              {/* Первый уровень: Карусель категорий с чипсами */}
              {categoryLevel === 1 && childrenCategoriesWithProducts && childrenCategoriesWithProducts.length > 0 && category?.slug && (
                <CarouselCategoriesPreview
                  className={styles['categories-top']}
                  categories={childrenCategoriesWithProducts}
                  parentCategorySlug={category?.slug}
                />
              )}

              {/* Второй+ уровень: Карусель категорий с чипсами */}
              {categoryLevel > 1 && categoriesBranch && categoriesBranch.length > 0 && (
                <CarouselCategoriesPreview
                  className={styles['categories-top']}
                  categories={categoriesBranch}
                  parentCategorySlug={breadcrumbChain.map(c => c.slug).join('/')}
                />
              )}

              <div className={styles['grid']}>
                {/* Первый уровень: Loader для каруселей (показываем сначала) */}
                {categoryLevel === 1 && childrenCategoriesLoading && (
                  <ProductCategoryCarouselLoading />
                )}

                {/* Первый уровень: Показываем карусели товаров по каждой подкатегории */}
                {categoryLevel === 1 && !childrenCategoriesLoading && childrenCategoriesWithProducts && childrenCategoriesWithProducts.length > 0
                  ? childrenCategoriesWithProducts.map((item) => {
                    const childPath = `${URL_PAGE_CATALOG}/${category?.slug}/${item?.slug}`
                    return (
                      <ProductCarousel
                        products={item?.products || []}
                        loading={loadingCategory}
                        title={item?.name}
                        link={childPath}
                        key={item?.id}
                      />
                    )
                  })
                  : null}

                {/* Первый уровень: Если нет подкатегорий - показываем товары */}
                {categoryLevel === 1 && !childrenCategoriesLoading && noSubCategories && (
                  <Products
                    categoryId={category?.id}
                    loadingCategory={loadingCategory}
                  />
                )}

                {/* Второй+ уровень: Всегда показываем товары */}
                {categoryLevel > 1 && (
                  <Products
                    categoryId={category?.id}
                    loadingCategory={loadingCategory}
                    onTotalChange={setTotalProducts}
                  />
                )}
              </div>
            </div>
          </div>
        </Wrapper>
      </PageGrid>
    </Container>
  )
}
